import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Collapse, Layout, Skeleton, Table } from 'antd';
import { Utils } from '../../../common/services/Utils';
import LayoutHeader from '../../../../components/LayoutHeader';
import { TestProjectRunCompareStore } from '../../stores';
import { TestRunComparisonItem, TestRunComparisonPackage } from '../../types';
import { TestProjectUtils } from '../../misc';
import { ColumnType } from 'antd/lib/table';

type Props = {
    store: TestProjectRunCompareStore;
};

const TestProjectRunCompareList: React.FC<Props> = ({ store }) => {
    const [openPanelKeys, setOpenPanelKeys] = React.useState<string[]>([]);

    const getTitle = () => {
        if (store.isLoading) {
            return <Skeleton active paragraph={{ rows: 0 }} />;
        }

        if (!store.comparisonResult) {
            return <></>;
        }

        const oldRunDate = Utils.formatDateStringShort(store.comparisonResult.oldRun.runTime);
        const newRunDate = Utils.formatDateStringShort(store.comparisonResult.newRun.runTime);

        const oldRunPercentage = TestProjectUtils.calculateResultVsBaseline(store.comparisonResult.oldRun);
        const newRunPercentage = TestProjectUtils.calculateResultVsBaseline(store.comparisonResult.newRun);

        const difference = newRunPercentage - oldRunPercentage;
        const differenceText = difference >= 0 ? `+${difference}%` : `${difference}%`;
        const differenceColor = difference >= 0 ? '#52c41a' : '#ff4d4f';

        return (
            <>
                Run Comparison: {oldRunDate} ({oldRunPercentage}%) → {newRunDate} ({newRunPercentage}%){' '}
                {difference !== 0 && <span style={{ color: differenceColor }}>{differenceText}</span>}
            </>
        );
    };

    const renderComparisonTable = (items: TestRunComparisonItem[]) => {
        const columns: ColumnType<TestRunComparisonItem>[] = [
            {
                title: 'Topic',
                dataIndex: 'topicName',
                key: 'topicName',
                width: '20%'
            },
            {
                title: 'Baseline (Older Run)',
                dataIndex: 'oldBaseline',
                key: 'oldBaseline',
                width: '20%',
                render: (value: string | undefined) => <span style={{ whiteSpace: 'pre-wrap' }}>{value ?? ''}</span>
            },
            {
                title: 'Result (Older Run)',
                dataIndex: 'oldResult',
                key: 'oldResult',
                width: '20%',
                render: (value: string | undefined) => <span style={{ whiteSpace: 'pre-wrap' }}>{value ?? ''}</span>
            },
            {
                title: 'Baseline (Newer Run)',
                dataIndex: 'newBaseline',
                key: 'newBaseline',
                width: '20%',
                render: (value: string | undefined) => <span style={{ whiteSpace: 'pre-wrap' }}>{value ?? ''}</span>
            },
            {
                title: 'Result (Newer Run)',
                dataIndex: 'newResult',
                key: 'newResult',
                width: '20%',
                render: (value: string | undefined) => <span style={{ whiteSpace: 'pre-wrap' }}>{value ?? ''}</span>
            }
        ];

        return (
            <Table
                tableLayout="fixed"
                columns={columns}
                dataSource={items}
                pagination={false}
                rowKey={(record: TestRunComparisonItem) => record.topicName}
            />
        );
    };

    const comparisonContent = () => {
        return (
            <Collapse
                className="collapse-with-sticky-header darker"
                activeKey={openPanelKeys}
                onChange={(keys: string[]) => setOpenPanelKeys(keys)}
            >
                {store.comparisonResult?.changedTopicsByPackage.map((pkg: TestRunComparisonPackage, index: number) => (
                    <Collapse.Panel
                        key={pkg.packageId ?? `${pkg.packageName}-${index}`}
                        header={<span style={{ fontWeight: 500 }}>{pkg.packageName}</span>}
                    >
                        {renderComparisonTable(pkg.items)}
                    </Collapse.Panel>
                ))}
            </Collapse>
        );
    };

    React.useEffect(() => {
        return () => store.abortComparisonPage();
    }, [store]);

    return (
        <Layout className="screen-size test-project-dashboard" style={{ ...{ height: '100%', background: 'white' } }}>
            <LayoutHeader
                title={getTitle()}
                subtitle={Utils.getSubtitle(store.project)}
                buttons={[
                    <Button
                        key="test-projects-compare-go-to-dashboard"
                        data-id="button-go-to-dashboard"
                        className="light"
                        size="large"
                        onClick={() => store.goToDashboard()}
                    >
                        Go to dashboard
                    </Button>
                ]}
            />
            <Layout>
                <Layout.Content style={{ maxHeight: 'calc(100vh - 100px)', overflow: 'auto' }}>
                    {store.isLoading ? (
                        <Skeleton active paragraph={{ rows: 4 }} title={{ style: { display: 'none' } }} />
                    ) : (
                        comparisonContent()
                    )}
                </Layout.Content>
            </Layout>
        </Layout>
    );
};

export default observer(TestProjectRunCompareList);
